// src/shared/voice-note.js — 🎤 record and play a voice note. ONE copy.
//
// v129.121. v129.119 built voice notes into the STAFF chat bundle
// (src/chat/index.js) and stopped there — but Head Office chat is a SEPARATE
// implementation (src/chatAdmin/index.js, `wac-` classes, its own composer and
// renderer), so Matt opened the Head Office view and there was no 🎤 at all.
//
// That is the THIRD time this exact shape has bitten in one week:
//   * v129.112 — Head Office's PDF: the staff opener was fixed in v125.81, the admin
//     app kept the broken one for two months.
//   * v129.117 — the close-the-loop email only understood stores, so every
//     area-manager report was unclearable.
//   * v129.119 — voice notes shipped to one of the two chat apps.
//
// So the recorder does NOT get copied into chatAdmin. It moves here, both
// bundles import it, and the next chat feature has one place to land. The
// callers keep their own MARKUP (the two apps have different class prefixes and
// different global namespaces); everything else lives in this file.

import { decodeDataUrl } from './open-file.js';

// The client cap. The Worker's MAX_VOICE_SECONDS is the real authority; this is
// what stops the recording before it can breach it.
export const VOICE_MAX_S = 90;
// ~90s of Opus at 32kbps is well under this; it is the belt to the server's
// braces (MAX_VOICE_CHARS), refused here with a plain reason rather than as a
// failed send.
export const VOICE_MAX_BYTES = 500 * 1024;

export function fmtDur(s) {
  var n = Math.max(0, Math.round(Number(s) || 0));
  return Math.floor(n / 60) + ':' + String(n % 60).padStart(2, '0');
}

export function voiceSupported() {
  return typeof MediaRecorder !== 'undefined'
    && typeof navigator !== 'undefined'
    && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
}

// Devices disagree: Chrome/Android will record audio/webm (Opus), iOS Safari
// records audio/mp4 (AAC). Both are on the Worker's whitelist; this picks
// whichever the browser will actually give us.
//
// v129.126 — THE ORDER MATTERS AND I HAD IT BACKWARDS. This list used to try
// webm/Opus first. But a voice note is not recorded for the sender, it is
// recorded for whoever opens it, and **Safari cannot play WebM at all**. So
// every note recorded on an Android or Chrome device was silent on every iPhone
// in the company — while playing back perfectly for the person who sent it,
// which is exactly the shape of Matt's report ("i can hear it, the other party
// says they cant").
//
// MP4/AAC is the only format both families play, so it is now first choice and
// WebM is the fallback for a browser that cannot record MP4. That makes the
// common case — a note recorded anywhere, opened anywhere — work.
//
// Notes ALREADY recorded as WebM stay WebM and stay unplayable on iPhones;
// nothing here can change that, so playVoiceInto now says so by name instead of
// presenting a dead player.
export function voiceMime() {
  var tries = ['audio/mp4;codecs=mp4a.40.2', 'audio/mp4', 'audio/webm;codecs=opus', 'audio/webm'];
  for (var i = 0; i < tries.length; i++) {
    try { if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(tries[i])) return tries[i]; } catch (e) { /* next */ }
  }
  return '';   // let the browser pick — the Worker validates whatever comes out
}

// ── The recorder ────────────────────────────────────────────────────────────
// One recording at a time, process-wide: two chat surfaces can be mounted in
// the same page, and two live MediaRecorders on one microphone is not a state
// worth supporting.
//
// Callbacks, all optional:
//   onTick(seconds)              — drive a live timer
//   onDone({dataUrl, duration})  — a finished recording, ready to send
//   onCancel()                   — discarded, or too short to be a note
//   onError(message)             — say WHICH fix applies; never a silent no-op
var _rec = null;

export function isRecording() { return !!_rec; }

export async function startRecording(cb) {
  var c = cb || {};
  if (_rec) return false;
  if (!voiceSupported()) {
    if (c.onError) c.onError('Voice notes are not supported on this device');
    return false;
  }
  var stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (e) {
    // Denied, or no microphone. The wording matters: "it doesn't work" reports
    // come from exactly this moment, and the fix is not in the app.
    if (c.onError) c.onError('Microphone blocked — allow it in the browser settings to send voice notes');
    return false;
  }
  var mt = voiceMime();
  var rec;
  try { rec = mt ? new MediaRecorder(stream, { mimeType: mt, audioBitsPerSecond: 32000 }) : new MediaRecorder(stream); }
  catch (e) { rec = new MediaRecorder(stream); }
  _rec = { rec: rec, chunks: [], t0: Date.now(), timer: null, stream: stream, discard: false, stopping: false, cb: c };
  rec.ondataavailable = function (ev) { if (ev.data && ev.data.size && _rec) _rec.chunks.push(ev.data); };
  rec.onstop = function () { finishRecording(); };
  rec.start();
  _rec.timer = setInterval(function () {
    if (!_rec) return;
    var secs = (Date.now() - _rec.t0) / 1000;
    if (_rec.cb.onTick) _rec.cb.onTick(secs);
    if (secs >= VOICE_MAX_S) stopRecording();      // hard stop; guarded below
  }, 250);
  return true;
}

export function stopRecording() {
  if (!_rec || _rec.stopping) return;
  _rec.stopping = true;
  try { _rec.rec.stop(); } catch (e) { finishRecording(); }
}

export function cancelRecording() {
  if (!_rec) return;
  _rec.discard = true;
  stopRecording();
}

function finishRecording() {
  var r = _rec;
  _rec = null;
  if (!r) return;
  if (r.timer) clearInterval(r.timer);
  // RELEASE the microphone. A stuck recording indicator in the browser chrome
  // is a privacy complaint waiting to happen.
  try { r.stream.getTracks().forEach(function (t) { t.stop(); }); } catch (e) { /* already gone */ }
  var secs = Math.round((Date.now() - r.t0) / 1000);
  var cb = r.cb || {};
  if (r.discard || !r.chunks.length || secs < 1) { if (cb.onCancel) cb.onCancel(); return; }
  var blob = new Blob(r.chunks, { type: r.rec.mimeType || 'audio/webm' });
  if (blob.size > VOICE_MAX_BYTES) {
    if (cb.onError) cb.onError('That voice note is too long — keep it under a minute and a half');
    if (cb.onCancel) cb.onCancel();
    return;
  }
  var reader = new FileReader();
  reader.onload = function () {
    if (cb.onDone) cb.onDone({ dataUrl: String(reader.result || ''), duration: Math.min(secs, VOICE_MAX_S) });
  };
  reader.onerror = function () {
    if (cb.onError) cb.onError('Could not read the recording — try again');
    if (cb.onCancel) cb.onCancel();
  };
  reader.readAsDataURL(blob);
}

// ── Playback ────────────────────────────────────────────────────────────────
// A data URL straight onto an <audio src> is the same silent-no-op family as
// the v129.112 download anchor, so it goes through a Blob object URL — set as a
// PROPERTY, never string-built into markup. Cached per message id: pressing
// play twice must not re-download the audio.
var _cache = {};
// v129.126 — the stored format, kept beside the blob URL so playback can say
// WHICH format a device is refusing rather than just going quiet.
var _mime = {};

export function voiceObjectUrl(id, dataUrl) {
  var key = String(id);
  if (_cache[key]) return _cache[key];
  var dec = decodeDataUrl(dataUrl);
  if (!dec) return null;
  try {
    _cache[key] = URL.createObjectURL(new Blob([dec.bytes], { type: dec.mime }));
    _mime[key] = dec.mime;
    return _cache[key];
  } catch (e) { return null; }
}

// Can this device play what is actually stored? '' from canPlayType is a
// DEFINITE no (Safari answers that for every audio/webm), so it is safe to act
// on; 'maybe' and 'probably' both mean try it.
export function canPlayStored(id) {
  try {
    var mime = _mime[String(id)];
    if (!mime || typeof document === 'undefined') return true;
    var probe = document.createElement('audio');
    if (!probe.canPlayType) return true;
    return probe.canPlayType(mime) !== '';
  } catch (e) { return true; }
}

// "audio/webm;codecs=opus" -> "WebM" — for a message a store colleague reads.
export function formatLabel(id) {
  var mime = String(_mime[String(id)] || '');
  if (mime.indexOf('webm') !== -1) return 'WebM';
  if (mime.indexOf('mp4') !== -1 || mime.indexOf('aac') !== -1) return 'MP4';
  if (mime.indexOf('ogg') !== -1) return 'Ogg';
  if (mime.indexOf('mpeg') !== -1) return 'MP3';
  return mime.replace(/^audio\//, '').replace(/;.*$/, '') || 'that format';
}

// Swap a ▶ stub for a real player. `fetchVoice(id)` is the caller's API call —
// injected, because the two chat apps authenticate differently (the staff app
// carries a read identity in the query string, Head Office a Bearer).
// v129.126 — the stub is re-found in the LIVE DOM after the download, never
// trusted across the await. The thread repaints on a 15-second poll, so a tap
// whose fetch straddled a repaint used to resolve against a detached button:
// replaceChild threw, the catch swallowed it, and the tap did nothing at all.
// One quiet tap is indistinguishable from "the voice note has no sound", which
// is how a recipient reports it.
function liveStub(id, fallback) {
  try {
    if (typeof document !== 'undefined') {
      var live = document.querySelector('[data-voice="' + String(id) + '"]');
      if (live) return live;
    }
  } catch (e) { /* fall through to what the caller handed us */ }
  return (fallback && fallback.isConnected !== false) ? fallback : null;
}

export async function playVoiceInto(id, stubEl, fetchVoice) {
  if (!stubEl) return null;
  var url = _cache[String(id)] || null;
  if (!url) {
    var prev = stubEl.textContent;
    stubEl.textContent = '🎤 loading…';
    var dataUrl = null;
    try { dataUrl = await fetchVoice(id); } catch (e) { dataUrl = null; }
    url = dataUrl ? voiceObjectUrl(id, dataUrl) : null;
    stubEl = liveStub(id, stubEl);          // the thread may have repainted
    if (!stubEl) return null;
    if (!url) {
      // Say it plainly rather than sitting on "loading…" forever.
      stubEl.textContent = '🎤 unavailable';
      if (prev) stubEl.title = prev;
      return null;
    }
  }
  // v129.126 — refuse BEFORE building a player that could only sit there mute.
  // This is the Android-recorded-note-on-an-iPhone case, named.
  if (!canPlayStored(id)) {
    stubEl.textContent = '🎤 ' + formatLabel(id) + ' not supported here';
    stubEl.title = 'This device cannot play ' + formatLabel(id) + ' audio. Ask them to re-record it, or open it on another phone.';
    return null;
  }
  var host = stubEl.parentElement;
  if (!host) return null;
  var au = document.createElement('audio');
  au.controls = true;
  au.autoplay = true;
  au.style.cssText = 'height:34px;max-width:230px;display:block';
  // v129.122 — the decode can succeed and the CODEC still be unplayable here:
  // Chrome records webm/Opus, and Safari will not play webm. Left alone that is
  // a silent dead player, which is the same class of fault as the v129.112
  // download anchor. Say it, and say whose device it is.
  au.onerror = function () {
    var note = document.createElement('div');
    note.style.cssText = 'font-size:12px;color:var(--adm-red-text);padding:4px 0';
    note.textContent = '🎤 This device cannot play that recording';
    try { au.replaceWith(note); } catch (e) { /* already detached */ }
  };
  au.src = url;                              // property, never markup
  try { host.replaceChild(au, stubEl); } catch (e) { return null; }
  // v129.134 — ANDROID (the ops manager's Galaxy A56, Android 16: "the audio doesnt
  // load"). `autoplay` alone is not enough here and the reason is subtle: the
  // element is created AFTER an await. The tap's transient user activation is
  // already spent by the time the download finishes — seconds, on a store's
  // connection — and Android Chrome's autoplay policy is far stricter than
  // desktop Chrome (media-engagement heuristics) or iOS Safari. So the player
  // appears, looks correct, and silently never starts. From the other side of
  // the shop that is "the voice note doesn't work".
  //
  // Ask explicitly, and treat a refusal as BLOCKED rather than broken: the
  // controls are already there, so the fix is to say "tap ▶" — a fresh gesture
  // always plays. Never a silent no-op, which is the v129.112 lesson again.
  try {
    var started = au.play();
    if (started && typeof started.catch === 'function') {
      started.catch(function () {
        try {
          if (au.parentElement && !au.parentElement.querySelector('[data-voice-hint]')) {
            var hint = document.createElement('div');
            hint.setAttribute('data-voice-hint', '1');
            hint.style.cssText = 'font-size:11px;color:var(--sub);padding:2px 0';
            hint.textContent = 'Tap ▶ to play';
            au.insertAdjacentElement('afterend', hint);
          }
        } catch (e2) { /* the controls are still there either way */ }
      });
    }
  } catch (e) { /* older browser without the promise form — controls remain */ }
  return url;
}

// The ▶ stub markup. Shared so both apps show the same thing — the duration
// comes from the thread metadata, so nothing is downloaded to render it.
export function voiceStubHtml(msgId, durationS, handlerFn) {
  // data-voice is how playVoiceInto re-finds this button after a repaint — see
  // liveStub. Without it a tap that straddles the 15s poll is silently lost.
  return '<div style="margin:2px 0"><button data-voice="' + Number(msgId) + '" onclick="' + handlerFn + '(' + Number(msgId) + ',this)" '
    + 'style="border:1px solid var(--border-strong);background:var(--surface-3);border-radius:16px;padding:7px 14px;font-size:13px;'
    + 'cursor:pointer;font-family:inherit">▶ 🎤 '
    + (durationS != null ? fmtDur(durationS) : 'Voice note') + '</button></div>';
}
